import { Outlet, Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X, Phone, Mail, MapPin, ArrowRight, Linkedin, Facebook, Instagram } from "lucide-react";
import { LOGO_URL, NAV_LINKS, CONTACT } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import AppointmentDialog from "@/components/AppointmentDialog";
import ChatbotWidget from "@/components/ChatbotWidget";

const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [bookOpen, setBookOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-40 border-b transition-all ${scrolled ? "glass border-gray-200/70 shadow-sm" : "bg-white/80 border-transparent"}`}
    >
      <div className="mx-auto max-w-7xl px-6 md:px-10 flex items-center justify-between h-20">
        <Link to="/" className="flex items-center gap-3" data-testid="header-logo-link">
          <img src={LOGO_URL} alt="Passerelle Numérique" className="h-12 w-12 object-contain" />
          <div className="hidden sm:block leading-tight">
            <div className="font-heading font-extrabold text-[#0B1F3A] text-base">PASSERELLE NUMÉRIQUE</div>
            <div className="text-[10px] tracking-[0.18em] uppercase text-[#00B8D9] font-semibold">Votre partenaire en croissance</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV_LINKS.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              end={link.to === "/"}
              data-testid={`nav-link-${link.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  isActive ? "text-[#00B8D9]" : "text-[#0B1F3A] hover:text-[#00B8D9]"
                }`
              }
            >
              {link.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <Button
            onClick={() => setBookOpen(true)}
            data-testid="header-cta-diagnostic"
            className="bg-[#00B8D9] hover:bg-[#0096B3] text-white font-semibold rounded-md px-5 py-2.5 shadow-sm"
          >
            Diagnostic gratuit
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>

        <button
          onClick={() => setOpen(!open)}
          className="lg:hidden p-2 rounded-md text-[#0B1F3A]"
          data-testid="mobile-menu-toggle"
          aria-label="Menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-gray-200 bg-white" data-testid="mobile-menu">
          <div className="px-6 py-4 space-y-1">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === "/"}
                className={({ isActive }) =>
                  `block px-3 py-3 rounded-md font-medium ${isActive ? "bg-[#F5F7FA] text-[#00B8D9]" : "text-[#0B1F3A]"}`
                }
              >
                {link.label}
              </NavLink>
            ))}
            <Button
              onClick={() => setBookOpen(true)}
              className="w-full mt-2 bg-[#00B8D9] hover:bg-[#0096B3] text-white font-semibold"
              data-testid="mobile-cta-diagnostic"
            >
              Diagnostic gratuit
            </Button>
          </div>
        </div>
      )}
      <AppointmentDialog open={bookOpen} onOpenChange={setBookOpen} defaultType="diagnostic" />
    </header>
  );
};

const Footer = () => (
  <footer className="bg-[#0B1F3A] text-white" data-testid="site-footer">
    <div className="mx-auto max-w-7xl px-6 md:px-10 py-16">
      <div className="grid md:grid-cols-4 gap-10">
        <div className="md:col-span-1">
          <div className="flex items-center gap-3 mb-4">
            <img src={LOGO_URL} alt="Logo" className="h-10 w-10 object-contain bg-white rounded p-1" />
            <div className="font-heading font-extrabold">PASSERELLE NUMÉRIQUE</div>
          </div>
          <p className="text-sm text-gray-300 leading-relaxed">
            Votre partenaire en croissance numérique. Web, IA et automatisation pour PME au Québec.
          </p>
          <div className="flex gap-3 mt-5">
            <a href="#" aria-label="LinkedIn" className="p-2 rounded-md bg-white/10 hover:bg-[#00B8D9] transition-colors"><Linkedin className="h-4 w-4" /></a>
            <a href="#" aria-label="Facebook" className="p-2 rounded-md bg-white/10 hover:bg-[#00B8D9] transition-colors"><Facebook className="h-4 w-4" /></a>
            <a href="#" aria-label="Instagram" className="p-2 rounded-md bg-white/10 hover:bg-[#00B8D9] transition-colors"><Instagram className="h-4 w-4" /></a>
          </div>
        </div>

        <div>
          <h4 className="font-heading font-bold mb-4">Navigation</h4>
          <ul className="space-y-2 text-sm text-gray-300">
            {NAV_LINKS.map((l) => (
              <li key={l.to}><Link to={l.to} className="hover:text-[#00B8D9]">{l.label}</Link></li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="font-heading font-bold mb-4">Liens utiles</h4>
          <ul className="space-y-2 text-sm text-gray-300">
            <li><Link to="/services/sites-web" className="hover:text-[#00B8D9]">Création de sites web</Link></li>
            <li><Link to="/services/seo" className="hover:text-[#00B8D9]">SEO & référencement local</Link></li>
            <li><Link to="/services/publicite" className="hover:text-[#00B8D9]">Publicité numérique</Link></li>
            <li><Link to="/services/automatisation-ia" className="hover:text-[#00B8D9]">Automatisation IA</Link></li>
            <li><Link to="/services/developpement" className="hover:text-[#00B8D9]">Développement d'apps</Link></li>
            <li><Link to="/cahier-des-charges" className="text-[#00B8D9] font-semibold hover:underline">Démarrer un projet →</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="font-heading font-bold mb-4">Contact</h4>
          <ul className="space-y-3 text-sm text-gray-300">
            <li className="flex items-center gap-2"><Phone className="h-4 w-4 text-[#00B8D9]" /><a href={CONTACT.phoneHref} className="hover:text-[#00B8D9]">{CONTACT.phone}</a></li>
            <li className="flex items-center gap-2"><Mail className="h-4 w-4 text-[#00B8D9]" /><a href={`mailto:${CONTACT.email}`} className="hover:text-[#00B8D9]">{CONTACT.email}</a></li>
            <li className="flex items-center gap-2"><MapPin className="h-4 w-4 text-[#00B8D9]" />{CONTACT.city}</li>
          </ul>
        </div>
      </div>

      <div className="mt-12 pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between gap-4 text-xs text-gray-400">
        <div>© {new Date().getFullYear()} Passerelle Numérique Inc. Tous droits réservés.</div>
        <div className="flex gap-6">
          <Link to="/admin/login" className="hover:text-[#00B8D9]" data-testid="footer-admin-link">Admin</Link>
          <Link to="/portail/login" className="hover:text-[#00B8D9]" data-testid="footer-portal-link">Portail client</Link>
          <span>Montréal · Québec · Canada</span>
        </div>
      </div>
    </div>
  </footer>
);

export default function Layout() {
  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      <main className="flex-1" data-testid="main-content">
        <Outlet />
      </main>
      <Footer />
      <ChatbotWidget />
    </div>
  );
}
